import {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {usePrevious} from '@continuum/ui/hooks';
import type {CanvasPanelView} from '../components/canvas-panel';
import {useIsDesktop, useIsLargeDesktop} from './use-breakpoints';

interface CanvasPanelState {
  activePanel: CanvasPanelView | null;
  visiblePanel: CanvasPanelView;
  isPanelOpen: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  setActivePanel: (panel: CanvasPanelView | null) => void;
  closePanel: () => void;
}

export function useCanvasPanel(): CanvasPanelState {
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsLargeDesktop();
  const wasDesktop = usePrevious(isDesktop);
  const wasLargeDesktop = usePrevious(isLargeDesktop);
  const [activePanel, setActivePanel] = useState<CanvasPanelView | null>(
    isLargeDesktop ? 'chat' : null,
  );

  useLayoutEffect(() => {
    // Closing on shrink avoids crowding a medium layout, or surprise-opening
    // as a mobile dialog after a resize.
    const shrankPastLarge = wasLargeDesktop && !isLargeDesktop;
    const shrankPastDesktop = wasDesktop && !isDesktop;
    if (shrankPastLarge || shrankPastDesktop) {
      setActivePanel(null);
    }
  }, [isDesktop, isLargeDesktop, wasDesktop, wasLargeDesktop]);

  // Keep rendering the last open view so closing from pages doesn't flash the
  // chat view during the exit animation.
  const lastOpenPanel = useRef<CanvasPanelView>('chat');
  if (activePanel !== null) {
    lastOpenPanel.current = activePanel;
  }

  const closePanel = useCallback(() => setActivePanel(null), []);

  return {
    activePanel,
    visiblePanel: activePanel ?? lastOpenPanel.current,
    isPanelOpen: activePanel !== null,
    isDesktop,
    isLargeDesktop,
    setActivePanel,
    closePanel,
  };
}
